import { useEffect, useState, useCallback } from "react";
import { MessageCircle, Download, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function monthLabel(m) {
  try {
    const [y, mo] = m.split("-");
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
  } catch { return m; }
}

function recentMonths(n = 12) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

export default function ClicksHistory() {
  const [month, setMonth] = useState("");
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState({});

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clicks-report", { params: m ? { month: m } : {} });
      setRep(data);
      setMonth(data.month);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const months = Array.from(new Set([...(rep?.available_months || []), ...recentMonths()]))
    .sort((a, b) => (a < b ? 1 : -1));

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/clicks-report/pdf", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cleartile-whatsapp-clicks-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(apiError(e)); }
    setExporting(false);
  };

  return (
    <div className="space-y-5" data-testid="clicks-history-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-[#25D366]" /> WhatsApp Clicks History
          </h1>
          <p className="text-muted-foreground text-sm font-medium">Seller-wise WhatsApp enquiry clicks, grouped by city. Export as PDF proof anytime.</p>
        </div>
        <div className="flex items-end gap-2">
          <Select value={month} onValueChange={(v) => load(v)}>
            <SelectTrigger className="w-[190px] font-semibold" data-testid="clicks-month-select">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m} data-testid={`month-opt-${m}`}>{monthLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportPdf} disabled={exporting || loading} className="gap-1.5 font-bold" data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export PDF
          </Button>
        </div>
      </div>

      {rep && (
        <div className="flex flex-wrap gap-3">
          <Stat label="Total Clicks" value={rep.total} />
          <Stat label="Cities" value={rep.cities.length} />
          <Stat label="Period" value={monthLabel(rep.month)} />
        </div>
      )}

      {loading ? (
        <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !rep || rep.cities.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground font-semibold" data-testid="clicks-empty">No WhatsApp clicks recorded for {monthLabel(month)}.</div>
      ) : (
        <div className="space-y-6">
          {rep.cities.map((c) => (
            <div key={c.city} className="bg-card border rounded-2xl overflow-hidden" data-testid={`city-block-${c.city}`}>
              <div className="flex items-center justify-between px-5 py-3 bg-secondary/50 border-b">
                <h2 className="font-heading font-extrabold text-lg">{c.city}</h2>
                <Badge className="bg-[#25D366]/15 text-[#128C4B] border-[#25D366]/30 font-bold">{c.total} clicks</Badge>
              </div>
              <div className="divide-y">
                {c.sellers.map((s) => {
                  const key = `${c.city}-${s.seller_id}`;
                  return (
                    <div key={key} className="px-5 py-3">
                      <button
                        onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                        className="w-full flex items-center justify-between gap-3 text-left"
                        data-testid={`seller-row-${s.seller_id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-heading font-bold text-sm truncate">{s.company_name}</div>
                          <div className="text-xs text-muted-foreground font-medium">{s.mobile}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="font-bold" data-testid={`seller-clicks-${s.seller_id}`}>{s.clicks} clicks</Badge>
                          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open[key] ? "rotate-180" : ""}`} />
                        </div>
                      </button>
                      {open[key] && (
                        <div className="mt-3 space-y-2 pl-1">
                          <div className="space-y-1.5">
                            {s.products.map((p, i) => (
                              <div key={i} className="flex items-center justify-between text-xs bg-secondary/40 rounded-lg px-3 py-1.5">
                                <span className="truncate text-muted-foreground font-medium">{p.title}</span>
                                <span className="font-bold shrink-0 ml-2">{p.clicks}</span>
                              </div>
                            ))}
                          </div>
                          {s.buyers && s.buyers.length > 0 && (
                            <div>
                              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Buyer mobiles who clicked</div>
                              <div className="flex flex-wrap gap-1.5" data-testid={`seller-buyers-${s.seller_id}`}>
                                {s.buyers.map((b, i) => (
                                  <span key={i} className="text-[11px] bg-[#25D366]/10 text-[#128C4B] font-semibold rounded-full px-2 py-0.5">{b.mobile} · {b.clicks}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border rounded-xl px-4 py-3 min-w-[120px]">
      <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">{label}</div>
      <div className="font-heading font-extrabold text-xl mt-0.5">{value}</div>
    </div>
  );
}
